import { useIsHeidi2OptInEnabled } from '@/app-shell/flags/agents-flags';
import { useIsAgentCreditAllowanceGateEnabled } from '@/app-shell/flags/commerce-flags';
import { useIsHeidi2UsageNudgesEnabled } from '@/app-shell/flags/growth-flags';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { useIsAppleCompliantBuild } from '@/hooks/useIsAppleCompliantBuild';
import {
  resolveWorkBillingAnalyticsProps,
  trackWorkUpgradeActioned,
} from '@/models/analytics/features/work';
import {
  type AgentCreditAllowanceBannerSurface,
  creditAllowanceRefusalAtom,
  creditAllowanceThresholdConsumedAtom,
  type CreditAllowanceThresholdSurface,
  type CreditUsagePopoverPlan,
  getCreditAllowanceThresholdBillingPlanKey,
  isCreditAllowanceThresholdBillingPlanResolved,
  isHeidi2OptInCreditUsageEnabled,
  isThresholdConsumedInRecord,
  markCreditAllowanceThresholdConsumedAtom,
  pricingPlansModalOpenAtom,
  relaxCreditAllowanceRefusalHold,
  resolveAgentCreditAllowanceBanner,
  resolveCreditUsagePopoverPlan,
  resolveCreditUsagePopoverTargetPlan,
  shouldForceEvidenceOnlyOnCreditExhausted,
  shouldSkipCreditAllowanceSend,
  useIsMaxBillingSeat,
  useQueryCreditAllowance,
} from '@heidi/commerce';
import {
  getCreditAllowanceNudgeVariant,
  useQueryUserBillingInfo,
  useReverseTrial,
} from '@heidi/commerce/data';

import { type SubscriptionTier } from '@heidi/schema/ml-scribe';
import { getDefaultStore, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { WorkSurface } from '@heidi/agents/contracts';

/** The Max plan the banner sells: Practice Max to a Practice seat, Clinician Max otherwise. */
function trackQuotaUpgrade(
  action: 'viewed' | 'cta_clicked' | 'dismissed',
  analyticsSurface: WorkSurface,
  targetPlan: SubscriptionTier
): void {
  trackWorkUpgradeActioned({
    action,
    entry_point: 'quota',
    surface: analyticsSurface,
    target_plan: targetPlan,
    target_seat_type: 'max',
    ...resolveWorkBillingAnalyticsProps(),
  });
}

/**
 * Emits work_upgrade_actioned `viewed` once per shown upgrade banner state.
 * Call from the component that renders the banner, not from the state hook:
 * the hook also backs the composer chrome, so an effect there double-counts.
 */
export function useTrackQuotaUpgradeViewed(
  surface: AgentCreditAllowanceBannerSurface | null,
  analyticsSurface: WorkSurface,
  plan: CreditUsagePopoverPlan = 'clinicianMax'
): void {
  const targetPlan = resolveCreditUsagePopoverTargetPlan(plan);
  const viewedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (surface == null || surface.kind !== 'upgrade') {
      viewedKeyRef.current = null;
      return;
    }
    // XXX: a non-dismissible banner is the forced-exhausted hold, a distinct view.
    const key = `${surface.threshold}:${surface.dismissible}`;
    if (viewedKeyRef.current === key) return;
    viewedKeyRef.current = key;
    trackQuotaUpgrade('viewed', analyticsSurface, targetPlan);
  }, [analyticsSurface, surface, targetPlan]);
}

export function useAgentCreditAllowanceBannerState(
  analyticsSurface: WorkSurface = 'project_chat'
) {
  const isHeidi2OptInEnabled = useIsHeidi2OptInEnabled();
  const isGateEnabled = useIsAgentCreditAllowanceGateEnabled();
  const isUsageNudgesEnabled = useIsHeidi2UsageNudgesEnabled();
  const hideCTA = useIsAppleCompliantBuild();
  const refusal = useAtomValue(creditAllowanceRefusalAtom, {
    store: getDefaultStore(),
  });
  const forceExhausted = Boolean(refusal?.holdExhausted);
  const { data: billingInfo } = useQueryUserBillingInfo();
  const isOptInCreditUsageEnabled = isHeidi2OptInCreditUsageEnabled({
    isHeidi2OptInEnabled,
    heidiVersion: billingInfo?.heidi_version,
  });
  // Keep GET live after a 402 even when the opt-in cohort is off, so the
  // send-hold can relax without a reload once remaining recovers.
  const query = useQueryCreditAllowance(isOptInCreditUsageEnabled || forceExhausted);
  const allowance = query.data;
  const dataUpdatedAt = query.dataUpdatedAt ?? 0;
  // Overage behaviour keys on the seat: Max never gets a credit banner.
  const isMaxSeat = useIsMaxBillingSeat();
  const { newUserTier } = useQueryUserTier();
  const { isClinicianPracticeTrialUser } = useReverseTrial();
  const plan = resolveCreditUsagePopoverPlan(newUserTier, isClinicianPracticeTrialUser);
  const targetPlan = resolveCreditUsagePopoverTargetPlan(plan);
  const consumedThresholds = useAtomValue(creditAllowanceThresholdConsumedAtom);
  const markThresholdConsumed = useSetAtom(markCreditAllowanceThresholdConsumedAtom);
  const setPricingPlansModalOpen = useSetAtom(pricingPlansModalOpenAtom);
  const threshold = getCreditAllowanceNudgeVariant(allowance);
  const ledgerSurface: CreditAllowanceThresholdSurface =
    analyticsSurface === 'workflow' ? 'workflow_banner' : 'banner';

  useEffect(() => {
    if (!refusal?.holdExhausted) return;
    if (!isGateEnabled) {
      relaxCreditAllowanceRefusalHold();
      return;
    }
    if (allowance == null) return;
    if (Number(allowance.remaining) <= 0) return;
    if (dataUpdatedAt < refusal.forcedAt) return;
    relaxCreditAllowanceRefusalHold();
  }, [allowance, dataUpdatedAt, isGateEnabled, refusal]);

  const billingPlanKey = isCreditAllowanceThresholdBillingPlanResolved(billingInfo)
    ? getCreditAllowanceThresholdBillingPlanKey(billingInfo)
    : null;

  // 402 hold paints 100 even when GET is still mid-ladder — dismiss must key
  // the ledger on that painted rung or close writes the wrong threshold.
  const ledgerThreshold = forceExhausted ? 100 : threshold;

  const isConsumed =
    allowance != null &&
    ledgerThreshold != null &&
    billingPlanKey != null &&
    isThresholdConsumedInRecord(
      consumedThresholds,
      billingPlanKey,
      allowance.cycle_end,
      ledgerThreshold,
      ledgerSurface
    );

  const surface = resolveAgentCreditAllowanceBanner({
    isOptInCreditUsageEnabled,
    isMaxSeat,
    threshold,
    isConsumed,
    forceExhausted,
    isUsageNudgesEnabled,
  });

  const skipSendArgs = {
    isOptInCreditUsageEnabled,
    isGateEnabled,
    isMaxSeat,
    threshold,
    forceExhausted,
    isUsageNudgesEnabled,
  };
  const sendDisabled = shouldSkipCreditAllowanceSend(skipSendArgs);
  const hideFollowUpQuestions = sendDisabled;
  const forceEvidenceOnlyOnSend = shouldForceEvidenceOnlyOnCreditExhausted(skipSendArgs);

  const handleDismiss = useCallback(() => {
    if (allowance == null || ledgerThreshold == null || billingPlanKey == null) {
      return;
    }
    trackQuotaUpgrade('dismissed', analyticsSurface, targetPlan);
    markThresholdConsumed({
      billingPlanKey,
      cycleEnd: allowance.cycle_end,
      threshold: ledgerThreshold,
      surface: ledgerSurface,
    });
  }, [
    allowance,
    analyticsSurface,
    billingPlanKey,
    ledgerSurface,
    ledgerThreshold,
    markThresholdConsumed,
    targetPlan,
  ]);

  const handleUpgradeClick = useCallback(() => {
    trackQuotaUpgrade('cta_clicked', analyticsSurface, targetPlan);
    setPricingPlansModalOpen(true);
  }, [analyticsSurface, setPricingPlansModalOpen, targetPlan]);

  return useMemo(
    () => ({
      surface,
      plan,
      hideCTA,
      hideFollowUpQuestions,
      sendDisabled,
      forceEvidenceOnlyOnSend,
      isUsageNudgesEnabled,
      cycleEnd: allowance?.cycle_end,
      onDismiss: handleDismiss,
      onUpgradeClick: handleUpgradeClick,
    }),
    [
      allowance?.cycle_end,
      forceEvidenceOnlyOnSend,
      handleDismiss,
      handleUpgradeClick,
      hideCTA,
      hideFollowUpQuestions,
      isUsageNudgesEnabled,
      plan,
      sendDisabled,
      surface,
    ]
  );
}
